// =============================================================================
// BookingModal — Reducer (single source of truth for wizard state)
// =============================================================================
//
// Key design choice: cascading resets are handled IN the reducer, not via
// useEffect.  e.g. SET_SERVICE_ID also clears smartConstraints, slot & error.
// This eliminates 5 sync-effects from the original monolith.
// =============================================================================

import { startOfDay } from 'date-fns';
import type {
  BookingState,
  BookingStep,
  BookingMode,
  NewCustomerForm,
  ResourceRequirementLocal,
  SmartConstraints,
  ExpandedSlot,
  AvailableSlot,
  OptimizeSuggestionResponse,
} from './types';
import {
  EMPTY_NEW_CUSTOMER,
  EMPTY_CUSTOM_EVENT,
  EMPTY_SMART_CONSTRAINTS,
} from './types';

// ===========================================================================
// Action Types
// ===========================================================================

export type BookingAction =
  // Lifecycle
  | { type: 'RESET'; payload: { slotDate: Date; manualStartTime: string } }
  | { type: 'SET_STEP'; payload: BookingStep }
  | { type: 'SET_BOOKING_MODE'; payload: BookingMode }
  // Customer
  | { type: 'SET_CUSTOMER_ID'; payload: string }
  | { type: 'SET_CUSTOMER_SEARCH'; payload: string }
  | { type: 'SET_SHOW_NEW_CUSTOMER_FORM'; payload: boolean }
  | { type: 'UPDATE_NEW_CUSTOMER'; payload: Partial<NewCustomerForm> }
  | { type: 'CUSTOMER_CREATED'; payload: string }
  // Service
  | { type: 'SET_SERVICE_ID'; payload: string }
  // Custom Event
  | { type: 'UPDATE_CUSTOM_EVENT'; payload: Partial<BookingState['customEvent']> }
  | { type: 'SET_SHOW_RESOURCE_PICKER'; payload: boolean }
  | { type: 'ADD_RESOURCE_REQUIREMENT'; payload: ResourceRequirementLocal }
  | { type: 'REMOVE_RESOURCE_REQUIREMENT'; payload: number }
  // Slot
  | { type: 'SET_SLOT_DATE'; payload: Date }
  | { type: 'SELECT_SLOT'; payload: AvailableSlot }
  | { type: 'SET_SHOW_MANUAL_TIME'; payload: boolean }
  | { type: 'SET_MANUAL_TIME'; payload: string }
  // Eventi di gruppo
  | { type: 'SET_MAX_CLIENTS_OVERRIDE'; payload: number | null }
  | { type: 'SET_JOIN_MODE'; payload: 'join' | 'create' }
  // Smart Constraints
  | { type: 'UPDATE_CONSTRAINTS'; payload: Partial<SmartConstraints> }
  | { type: 'RESET_CONSTRAINTS' }
  | { type: 'SET_RESOURCE_OVERRIDE'; payload: { slotIndex: number; resourceId: string | null } }
  | { type: 'SYNC_OVERRIDDEN_REQUIREMENTS'; payload: ExpandedSlot[] }
  // Optimization
  | { type: 'SET_OPTIMIZATION'; payload: OptimizeSuggestionResponse | null }
  | { type: 'SET_SHOW_OPTIMIZATION_PANEL'; payload: boolean }
  // Error
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' };

// ===========================================================================
// Initial State Factory
// ===========================================================================

export function createInitialState(slotDate: Date): BookingState {
  return {
    step: 'customer-service',
    bookingMode: 'catalog',
    selectedCustomerId: '',
    selectedServiceId: '',
    customerSearch: '',
    showNewCustomerForm: false,
    newCustomer: { ...EMPTY_NEW_CUSTOMER },
    customEvent: { ...EMPTY_CUSTOM_EVENT },
    showResourcePicker: false,
    slotDate: startOfDay(slotDate),
    selectedAvailableSlot: null,
    showManualTimeInput: false,
    manualStartTime: '',
    maxClientsOverride: null,
    joinMode: 'join',
    smartConstraints: { ...EMPTY_SMART_CONSTRAINTS },
    optimizationSuggestion: null,
    showOptimizationPanel: false,
    submitError: null,
  };
}

// ===========================================================================
// Reducer
// ===========================================================================

export function bookingReducer(state: BookingState, action: BookingAction): BookingState {
  switch (action.type) {
    // ── Lifecycle ──────────────────────────────────────────────────────
    case 'RESET':
      return {
        ...createInitialState(action.payload.slotDate),
        manualStartTime: action.payload.manualStartTime,
      };

    case 'SET_STEP':
      return { ...state, step: action.payload };

    case 'SET_BOOKING_MODE':
      // Cascade: reset everything downstream when mode flips
      return {
        ...state,
        bookingMode: action.payload,
        selectedAvailableSlot: null,
        selectedServiceId: '',
        selectedCustomerId: '',
        smartConstraints: { ...EMPTY_SMART_CONSTRAINTS },
        submitError: null,
        showManualTimeInput: false,
        manualStartTime: '',
        maxClientsOverride: null,
        joinMode: 'join',
      };

    // ── Customer ──────────────────────────────────────────────────────
    case 'SET_CUSTOMER_ID':
      return { ...state, selectedCustomerId: action.payload };

    case 'SET_CUSTOMER_SEARCH':
      return { ...state, customerSearch: action.payload };

    case 'SET_SHOW_NEW_CUSTOMER_FORM':
      return { ...state, showNewCustomerForm: action.payload };

    case 'UPDATE_NEW_CUSTOMER':
      return { ...state, newCustomer: { ...state.newCustomer, ...action.payload } };

    case 'CUSTOMER_CREATED':
      return {
        ...state,
        selectedCustomerId: action.payload,
        showNewCustomerForm: false,
        newCustomer: { ...EMPTY_NEW_CUSTOMER },
      };

    // ── Service ───────────────────────────────────────────────────────
    case 'SET_SERVICE_ID':
      // Cascade: clear constraints, slot & error when service changes
      return {
        ...state,
        selectedServiceId: action.payload,
        smartConstraints: { ...EMPTY_SMART_CONSTRAINTS },
        selectedAvailableSlot: null,
        submitError: null,
        maxClientsOverride: null,
        joinMode: 'join',
      };

    // ── Custom Event ──────────────────────────────────────────────────
    case 'UPDATE_CUSTOM_EVENT':
      return { ...state, customEvent: { ...state.customEvent, ...action.payload } };

    case 'SET_SHOW_RESOURCE_PICKER':
      return { ...state, showResourcePicker: action.payload };

    case 'ADD_RESOURCE_REQUIREMENT': {
      const exists = state.customEvent.resourceRequirements.some(
        r => r.resourceTypeId === action.payload.resourceTypeId,
      );
      if (exists) return state;
      return {
        ...state,
        customEvent: {
          ...state.customEvent,
          resourceRequirements: [...state.customEvent.resourceRequirements, action.payload],
        },
        showResourcePicker: false,
      };
    }

    case 'REMOVE_RESOURCE_REQUIREMENT':
      return {
        ...state,
        customEvent: {
          ...state.customEvent,
          resourceRequirements: state.customEvent.resourceRequirements.filter(
            (_, i) => i !== action.payload,
          ),
        },
      };

    // ── Slot ──────────────────────────────────────────────────────────
    case 'SET_SLOT_DATE':
      return {
        ...state,
        slotDate: action.payload,
        selectedAvailableSlot: null,
        submitError: null,
        joinMode: 'join',
      };

    case 'SELECT_SLOT':
      return {
        ...state,
        selectedAvailableSlot: action.payload,
        showManualTimeInput: false,
        submitError: null,
        joinMode: 'join',
      };

    case 'SET_SHOW_MANUAL_TIME':
      return {
        ...state,
        showManualTimeInput: action.payload,
        ...(action.payload && { selectedAvailableSlot: null }),
      };

    case 'SET_MANUAL_TIME':
      return { ...state, manualStartTime: action.payload, submitError: null };

    // ── Eventi di gruppo ──────────────────────────────────────────────
    case 'SET_MAX_CLIENTS_OVERRIDE':
      return { ...state, maxClientsOverride: action.payload };

    case 'SET_JOIN_MODE':
      return { ...state, joinMode: action.payload, submitError: null };

    // ── Smart Constraints ─────────────────────────────────────────────
    case 'UPDATE_CONSTRAINTS':
      return {
        ...state,
        smartConstraints: { ...state.smartConstraints, ...action.payload },
      };

    case 'RESET_CONSTRAINTS':
      return { ...state, smartConstraints: { ...EMPTY_SMART_CONSTRAINTS } };

    case 'SET_RESOURCE_OVERRIDE': {
      const updated = [...state.smartConstraints.overriddenRequirements];
      if (updated[action.payload.slotIndex]) {
        updated[action.payload.slotIndex] = {
          ...updated[action.payload.slotIndex],
          resourceId: action.payload.resourceId,
        };
      }
      return {
        ...state,
        smartConstraints: { ...state.smartConstraints, overriddenRequirements: updated },
      };
    }

    case 'SYNC_OVERRIDDEN_REQUIREMENTS': {
      // Merge expanded slots with existing overrides, preserving user selections
      const expandedSlots = action.payload;
      const next = expandedSlots.map((slot, idx) => {
        const existing = state.smartConstraints.overriddenRequirements[idx];
        if (existing && existing.resourceTypeId === slot.resourceTypeId) return existing;
        return { resourceTypeId: slot.resourceTypeId, resourceId: null };
      });
      // Bail out if nothing actually changed (prevents re-render loop)
      if (
        next.length === state.smartConstraints.overriddenRequirements.length &&
        next.every(
          (n, i) =>
            n.resourceTypeId === state.smartConstraints.overriddenRequirements[i]?.resourceTypeId &&
            n.resourceId === state.smartConstraints.overriddenRequirements[i]?.resourceId,
        )
      ) {
        return state; // Same reference → no re-render
      }
      return {
        ...state,
        smartConstraints: { ...state.smartConstraints, overriddenRequirements: next },
      };
    }

    // ── Optimization ──────────────────────────────────────────────────
    case 'SET_OPTIMIZATION':
      return { ...state, optimizationSuggestion: action.payload };

    case 'SET_SHOW_OPTIMIZATION_PANEL':
      return { ...state, showOptimizationPanel: action.payload };

    // ── Error ─────────────────────────────────────────────────────────
    case 'SET_ERROR':
      return { ...state, submitError: action.payload };

    case 'CLEAR_ERROR':
      return { ...state, submitError: null };

    default:
      return state;
  }
}
